import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { IconChevronRight, IconFolder, IconSearch, IconX } from '@tabler/icons-react';

import { SaleBidderLevel } from '__generated__/globalTypes';
import { getFileIcon } from 'utils/fileIconUtils';

import {
  countDataRoomDocuments,
  countPoolDocuments,
  countSaleKeyDocuments,
  filterDataRoom,
  filterDataRoomDocuments,
  isDataRoomEmpty,
  SaleDataRoom,
  SaleDataRoomDocument,
} from './saleDataRoom';
import {
  DOCUMENT_TYPE_FILTERS,
  documentTypeFilterLabel,
  documentTypeLabel,
  DocumentTypeFilter,
  matchesDocumentTypeFilter,
} from './saleDocumentTypes';

const LEVEL_BADGE: Record<SaleBidderLevel, string> = {
  [SaleBidderLevel.L1]:
    'border-background-subtle bg-background-subtle text-gray-300 light:border-gray-200 light:bg-gray-100 light:text-gray-700',
  [SaleBidderLevel.L2]:
    'border-accent-default/60 text-pink-200 light:border-pink-700/50 light:text-pink-700',
};

const LEVEL_TITLE: Record<SaleBidderLevel, string> = {
  [SaleBidderLevel.L1]: 'Level 1: general information, no PII',
  [SaleBidderLevel.L2]: 'Level 2: contains PII',
};

export const LevelBadge: FC<{ level: SaleBidderLevel; testId?: string }> = ({ level, testId }) => (
  <span
    data-testid={testId}
    title={LEVEL_TITLE[level]}
    className={`h-[18px] flex-none rounded-full border px-[7px] font-heebo text-[11px] font-medium leading-4 ${LEVEL_BADGE[level] ?? ''}`}
  >
    {level}
  </span>
);

export const VIEW_ACTION_CLASS =
  'inline-flex h-[30px] items-center gap-1.5 rounded border border-accent-default bg-accent-default/10 px-3 font-heebo text-[13px] font-medium text-pink-200 hover:bg-accent-default/20 disabled:cursor-not-allowed disabled:opacity-50 light:border-pink-700 light:bg-pink-700/[0.06] light:text-pink-700 light:hover:bg-pink-700/10';

export const DOWNLOAD_ACTION_CLASS =
  'inline-flex h-[30px] items-center gap-1.5 rounded border border-transparent px-2.5 font-heebo text-[13px] text-gray-500 hover:border-background-subtle hover:text-white disabled:cursor-not-allowed disabled:opacity-50 light:text-gray-700 light:hover:border-gray-200 light:hover:text-gray-900';

export const SEARCH_LABEL = 'Search the data room';

export const searchCountCopy = (shown: number, total: number) =>
  `${shown.toLocaleString()} of ${total.toLocaleString()} document${total === 1 ? '' : 's'}`;

export const noMatchCopy = (query: string) => `No documents or folders match "${query}".`;

export const noTypeMatchCopy = (filterLabel: string) =>
  `No documents match the ${filterLabel} filter.`;

type RenderDocumentAction = (document: SaleDataRoomDocument) => ReactNode;

// The type and actions columns line up across every depth, so depth indents the name cell only.
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_170px_212px] items-center gap-3 px-3';
const ROW_BORDER = 'border-b border-background-subtle/60 light:border-gray-100';
const INDENT_PX = 22;

const indent = (depth: number) => ({ paddingLeft: depth * INDENT_PX });

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightMatches = (text: string, query: string): ReactNode => {
  if (!query) {
    return text;
  }
  // A capturing split puts every match at an odd index.
  return text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi')).map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className="rounded-sm bg-yellow-300 text-gray-900">
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

const DocumentList: FC<{
  documents: SaleDataRoomDocument[];
  depth: number;
  query: string;
  renderDocumentAction?: RenderDocumentAction;
}> = ({ documents, depth, query, renderDocumentAction }) => (
  <>
    {documents.map((document) => (
      <li
        key={document.document_id}
        className={`${ROW_GRID} ${ROW_BORDER} min-h-10 py-1 hover:bg-background-subtle/40 light:hover:bg-gray-50`}
        data-testid="data-room-document"
        data-document-id={document.document_id}
        data-level={document.level}
      >
        <div className="flex min-w-0 items-center gap-2" style={indent(depth)}>
          <span aria-hidden="true" className="w-4 flex-none" />
          <span
            aria-hidden="true"
            className="flex-none text-slate-300 light:text-slate-400 [&>svg]:size-[18px]"
          >
            {getFileIcon(document.file_name)}
          </span>
          <span
            title={document.file_name}
            data-testid="data-room-document-name"
            className="truncate font-heebo text-sm text-white light:text-gray-900"
          >
            {highlightMatches(document.file_name, query)}
          </span>
          <LevelBadge level={document.level} testId="data-room-document-level" />
        </div>
        <div className="truncate font-heebo text-[13px] text-gray-500 light:text-gray-700">
          {highlightMatches(documentTypeLabel(document.doc_type), query)}
        </div>
        <div className="flex justify-end gap-1.5">{renderDocumentAction?.(document)}</div>
      </li>
    ))}
  </>
);

const Folder: FC<{
  name: string;
  depth: number;
  documentCount: number;
  query: string;
  revealKey: string;
  highlight?: boolean;
  children: ReactNode;
}> = ({ name, depth, documentCount, query, revealKey, highlight = true, children }) => {
  const ref = useRef<HTMLDetailsElement>(null);
  // `open` is only the initial state of a <details>, so a folder the user collapsed would stay
  // collapsed and hide the matches it was kept for.
  useEffect(() => {
    if (revealKey && ref.current) {
      ref.current.open = true;
    }
  }, [revealKey]);

  return (
    <li>
      <details ref={ref} open>
        <summary
          data-testid="data-room-folder"
          className={`${ROW_GRID} ${ROW_BORDER} min-h-10 cursor-pointer list-none bg-background-surface/50 py-1 hover:bg-background-subtle/40 light:bg-gray-50 light:hover:bg-gray-100 [&::-webkit-details-marker]:hidden`}
        >
          <span className="col-span-3 flex min-w-0 items-center gap-2" style={indent(depth)}>
            <IconChevronRight
              aria-hidden="true"
              size={16}
              className="flex-none text-gray-500 transition-transform light:text-gray-700 [details[open]>summary_&]:rotate-90"
            />
            <IconFolder
              aria-hidden="true"
              size={18}
              stroke={1.75}
              className="flex-none text-warning-default light:text-warning-muted"
            />
            <span
              title={name}
              className="truncate font-heebo text-sm font-medium text-gray-100 light:text-gray-900"
            >
              {highlight ? highlightMatches(name, query) : name}
            </span>
            {/* Drawn from the attribute so the summary's text stays the folder name alone. */}
            <span
              data-document-count={documentCount.toLocaleString()}
              className="h-[18px] flex-none rounded-full bg-background-subtle px-[7px] font-heebo text-[11px] leading-[18px] text-gray-400 after:content-[attr(data-document-count)] light:bg-gray-100 light:text-gray-700"
            />
          </span>
        </summary>
        <ul>{children}</ul>
      </details>
    </li>
  );
};

const TOOLBAR_BUTTON_CLASS =
  'h-[30px] rounded border border-background-subtle px-3 font-heebo text-[13px] text-gray-400 hover:text-white light:border-gray-200 light:text-gray-700 light:hover:text-gray-900';

const chipClass = (pressed: boolean) =>
  `h-[30px] rounded-full border px-3 font-heebo text-[13px] ${
    pressed
      ? 'border-accent-default bg-accent-default/10 text-pink-200 light:border-pink-700 light:bg-pink-700/[0.06] light:text-pink-700'
      : 'border-background-subtle text-gray-400 hover:text-white light:border-gray-200 light:text-gray-700 light:hover:text-gray-900'
  }`;

export const SaleDataRoomTree: FC<{
  dataRoom: SaleDataRoom;
  renderDocumentAction?: RenderDocumentAction;
  typeFilter?: DocumentTypeFilter;
  onTypeFilterChange?: (filter: DocumentTypeFilter) => void;
  query?: string;
  onQueryChange?: (query: string) => void;
}> = ({
  dataRoom,
  renderDocumentAction,
  typeFilter: controlledTypeFilter,
  onTypeFilterChange,
  query: controlledQuery,
  onQueryChange,
}) => {
  const [ownInput, setOwnInput] = useState('');
  const input = controlledQuery ?? ownInput;
  const setInput = onQueryChange ?? setOwnInput;
  const [ownTypeFilter, setOwnTypeFilter] = useState(DocumentTypeFilter.ALL);
  const typeFilter = controlledTypeFilter ?? ownTypeFilter;
  const setTypeFilter = onTypeFilterChange ?? setOwnTypeFilter;
  const inputRef = useRef<HTMLInputElement>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const query = input.trim();
  const typeFiltered = typeFilter !== DocumentTypeFilter.ALL;
  const visible = useMemo(() => {
    const ofType = typeFiltered
      ? filterDataRoomDocuments(dataRoom, (document) =>
          matchesDocumentTypeFilter(typeFilter, document.doc_type),
        )
      : dataRoom;
    return filterDataRoom(ofType, query);
  }, [dataRoom, query, typeFilter, typeFiltered]);
  const filtering = Boolean(query) || typeFiltered;
  const revealKey = filtering ? `${typeFilter}\n${query}` : '';
  const typeFilterLabel = documentTypeFilterLabel(typeFilter);

  const setAllFoldersOpen = (open: boolean) => {
    treeRef.current?.querySelectorAll('details').forEach((details) => {
      details.open = open;
    });
  };

  const folderProps = { query, revealKey };

  return (
    <div className="flex max-w-[1040px] flex-col gap-3 font-heebo">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-[1_1_420px]">
          <label className="flex h-10 items-center gap-2 rounded border border-gray-800 bg-background-canvas px-3 focus-within:border-accent-default light:border-gray-300 light:bg-white light:focus-within:border-pink-700">
            <span className="sr-only">{SEARCH_LABEL}</span>
            <IconSearch
              aria-hidden="true"
              size={18}
              stroke={1.75}
              className="pointer-events-none flex-none text-gray-500 light:text-gray-700"
            />
            <input
              ref={inputRef}
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="File name, document type, 4C ID or pool"
              className="min-w-0 flex-1 bg-transparent pr-6 text-sm text-white outline-none placeholder:text-gray-600 light:text-gray-900 light:placeholder:text-gray-600 [&::-webkit-search-cancel-button]:appearance-none"
            />
          </label>
          {input && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setInput('');
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white light:text-gray-700 light:hover:text-gray-900"
            >
              <IconX aria-hidden="true" size={16} />
            </button>
          )}
        </div>
        <p aria-live="polite" className="text-[13px] text-gray-400 light:text-gray-700">
          {filtering &&
            `${searchCountCopy(countDataRoomDocuments(visible), countDataRoomDocuments(dataRoom))}${
              typeFiltered ? ` · ${typeFilterLabel}` : ''
            }`}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Document type" className="flex flex-wrap gap-2">
          {DOCUMENT_TYPE_FILTERS.map(({ filter, label }) => (
            <button
              key={filter}
              type="button"
              aria-pressed={typeFilter === filter}
              onClick={() => setTypeFilter(filter)}
              className={chipClass(typeFilter === filter)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAllFoldersOpen(true)}
            className={TOOLBAR_BUTTON_CLASS}
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setAllFoldersOpen(false)}
            className={TOOLBAR_BUTTON_CLASS}
          >
            Collapse all
          </button>
        </div>
      </div>

      {filtering && isDataRoomEmpty(visible) ? (
        <p className="text-sm text-gray-400 light:text-gray-700">
          {query ? noMatchCopy(query) : noTypeMatchCopy(typeFilterLabel)}
        </p>
      ) : (
        <div
          ref={treeRef}
          className="overflow-hidden rounded-md border border-background-subtle bg-background-canvas light:border-gray-200 light:bg-white"
        >
          <div
            aria-hidden="true"
            className={`${ROW_GRID} h-9 border-b border-background-subtle bg-background-surface/50 text-[11px] uppercase tracking-[0.08em] text-gray-500 light:border-gray-200 light:bg-gray-50 light:text-gray-700`}
          >
            <span>Name</span>
            <span>Document type</span>
            <span className="text-right">Actions</span>
          </div>
          <ul aria-label="Data room">
            <Folder
              name={visible.folder_name}
              depth={0}
              documentCount={countDataRoomDocuments(visible)}
              highlight={false}
              {...folderProps}
            >
              <DocumentList
                documents={visible.documents}
                depth={1}
                query={query}
                renderDocumentAction={renderDocumentAction}
              />
              {visible.sale_keys.map((saleKey) => (
                <Folder
                  key={saleKey.sale_key}
                  name={saleKey.folder_name}
                  depth={1}
                  documentCount={countSaleKeyDocuments(saleKey)}
                  {...folderProps}
                >
                  {saleKey.pools.map((pool) => (
                    <Folder
                      key={pool.offering_id}
                      name={pool.folder_name}
                      depth={2}
                      documentCount={countPoolDocuments(pool)}
                      {...folderProps}
                    >
                      <DocumentList
                        documents={pool.documents}
                        depth={3}
                        query={query}
                        renderDocumentAction={renderDocumentAction}
                      />
                      {pool.assets.map((asset) => (
                        <Folder
                          key={asset.loan_id}
                          name={asset.folder_name}
                          depth={3}
                          documentCount={asset.documents.length}
                          {...folderProps}
                        >
                          <DocumentList
                            documents={asset.documents}
                            depth={4}
                            query={query}
                            renderDocumentAction={renderDocumentAction}
                          />
                        </Folder>
                      ))}
                    </Folder>
                  ))}
                </Folder>
              ))}
            </Folder>
          </ul>
        </div>
      )}
    </div>
  );
};
